
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Tag from '../../components/base/Tag';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { supabase, apiClient } from '../../lib/supabase';

export default function Home() {
  const [links, setLinks] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [announcement, setAnnouncement] = useState('');
  const [verificationCounts, setVerificationCounts] = useState({
    unanswered: 0,
    lies: 0,
    movements: 0,
  });
  const [verificationData, setVerificationData] = useState([]);
  const [upcomingEvents] = useState([
    {
      id: 1,
      title: '県政透明化を求める市民集会',
      location: '神戸市民会館',
      description: '県政運営の透明性向上を求める市民集会を開催します',
      startDateTime: '2024-03-15T14:00:00',
      slug: 'citizen-meeting-transparency',
    },
    {
      id: 2,
      title: '斎藤県政検証セミナー',
      location: '姫路市文化センター',
      description: '県政運営の問題点について専門家による検証セミナー',
      startDateTime: '2024-03-22T13:30:00',
      slug: 'verification-seminar',
    },
    {
      id: 3,
      title: '県民対話集会',
      location: '明石市民会館',
      description: '県民の声を直接聞く対話集会を開催',
      startDateTime: '2024-03-28T15:00:00',
      slug: 'citizen-dialogue',
    },
  ]);
  const [latestForumTopics] = useState([
    {
      id: 1,
      title: '県政運営の透明性について',
      body: '最近の県政運営において、意思決定プロセスの透明性に疑問を感じています。皆さんはどう思われますか？',
      author: '市民A',
      category: '政治',
      repliesCount: 15,
      updatedAt: '2024-01-20T10:30:00',
      slug: 'transparency-discussion',
      pinned: true,
    },
    {
      id: 2,
      title: '予算配分の優先順位について議論',
      body: '新年度予算案について、教育や福祉への配分が適切か議論しましょう',
      author: '関心を持つ県民',
      category: '予算',
      repliesCount: 8,
      updatedAt: '2024-01-19T16:45:00',
      slug: 'budget-priorities',
      pinned: false,
    },
  ]);

  // SEO設定
  useEffect(() => {
    document.title = "兵庫県政検証サイト - 斎藤元彦知事の問題を事実に基づき検証";

    // Schema.org JSON-LD for HomePage
    const homePageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "兵庫県政検証サイト",
      "description": "兵庫県知事斎藤元彦の県政運営の問題点を事実に基づいて整理・追跡する検証サイトです。",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
      "inLanguage": "ja",
      "isPartOf": {
        "@type": "WebSite",
        "name": "兵庫県政検証サイト",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      "mainEntity": {
        "@type": "Organization",
        "name": "兵庫県政検証サイト",
        "description": "県政運営の問題点を事実に基づいて整理・追跡",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      }
    };

    let existingScript = document.querySelector('script[data-schema="homepage"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'homepage');
    script.textContent = JSON.stringify(homePageSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[data-schema="homepage"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Supabaseからデータを取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        // お知らせデータを取得
        const announcementData = await apiClient.announcements.getActive();
        if (announcementData) {
          setAnnouncement(announcementData.message);
        }

        // リンクデータを取得
        const { data: linksData, error: linksError } = await supabase
          .from('links')
          .select('*')
          .order('created_at', { ascending: false });

        if (linksError) {
          console.error('Links fetch error:', linksError);
        } else {
          setLinks(linksData || []);
        }

        // ブログデータを取得
        const { data: blogsData, error: blogsError } = await supabase
          .from('blogs')
          .select('*')
          .order('published_at', { ascending: false });

        if (blogsError) {
          console.error('Blogs fetch error:', blogsError);
        } else {
          setBlogs(blogsData || []);
        }

        // 検証データを取得
        const { data: verificationResponse, error: verificationError } = await supabase
          .from('verification_data')
          .select('*')
          .order('date', { ascending: false });

        if (verificationError) {
          console.error('Verification data fetch error:', verificationError);
        } else {
          setVerificationData(verificationResponse || []);
          
          // 検証データの件数を計算
          const counts = { unanswered: 0, lies: 0, movements: 0 };
          (verificationResponse || []).forEach((item) => {
            if (item.data_type === 'unanswered' || item.category === '未回答事項') {
              counts.unanswered++;
            } else if (item.data_type === 'lies' || item.category === '虚偽・誇張') {
              counts.lies++;
            } else if (item.data_type === 'movements' || item.category === '関連動向') {
              counts.movements++;
            }
          });
          setVerificationCounts(counts);
        }
      } catch (error) {
        console.error('Data fetch error:', error);
      }
    };

    fetchData();

    const linksSubscription = supabase
      .channel('links_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'links' }, () => fetchData())
      .subscribe();

    const blogsSubscription = supabase
      .channel('blogs_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'blogs' }, () => fetchData())
      .subscribe();

    const verificationSubscription = supabase
      .channel('verification_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'verification_data' }, () => fetchData())
      .subscribe();

    return () => {
      linksSubscription.unsubscribe();
      blogsSubscription.unsubscribe();
      verificationSubscription.unsubscribe();
    };
  }, []);

  const featuredLinks = links.filter((link) => link.is_featured).slice(0, 6);
  const latestBlogs = blogs.slice(0, 3);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-blue-50 dark:bg-gray-800 py-16">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              'url("https://public.readdy.ai/ai/img_res/2c9fb334c55b157eb657d811c4da58aa.jpg")',
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-8">
              暴君による兵庫県政の<br />私物化を許すな
            </h1>
            <p className="text-xl md:text-2xl text-blue-700 dark:text-blue-300 mb-6 max-w-4xl mx-auto leading-relaxed">
              ２馬力デマ選挙で知事の座をかすめ取り、「自分が知事でいること」だけを目的に権力を過度に行使している斎藤元彦。その行為と真実をファクトに基づき徹底的に可視化するサイトです。
            </p>
          </div>
        </div>
      </section>

      {/* Announcement Section */}
      {announcement && (
        <section className="bg-gradient-to-r from-red-600 to-red-700 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3">
                <i className="ri-notification-2-line text-white text-2xl animate-pulse"></i>
                <p className="text-white text-2xl md:text-3xl font-bold">{announcement}</p>
                <i className="ri-notification-2-line text-white text-2xl animate-pulse"></i>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
        {/* Latest Blog Posts */}
        <section className="relative">
          <div className="flex items-center mb-12">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-emerald-300 to-emerald-300 dark:via-emerald-600 dark:to-emerald-300"></div>
            <div className="px-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                  <i className="ri-article-line text-emerald-600 dark:text-emerald-400"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white bg-white dark:bg-gray-900 px-4">最新の記事</h2>
              </div>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-emerald-300 to-transparent dark:from-emerald-600 dark:to-transparent"></div>
            <Link to="/blog" className="ml-4 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 font-medium">
              すべて見る →
            </Link>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {latestBlogs.map((blog) => (
              <Card key={blog.id} className="overflow-hidden hover:shadow-lg transition-all duration-200 hover:scale-105">
                <div className="p-6">
                  <Tag variant="category" className="mb-3">
                    {blog.category}
                  </Tag>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {blog.url ? (
                      <a
                        href={blog.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-emerald-600 dark:hover:text-emerald-400 cursor-pointer"
                      >
                        {blog.title}
                      </a>
                    ) : (
                      <Link to={`/blog#blog-${blog.id}`} className="hover:text-emerald-600 dark:hover:text-emerald-400">
                        {blog.title}
                      </Link>
                    )}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{blog.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                    <span>by {blog.author}</span>
                    <span>{new Date(blog.published_at).toLocaleDateString('ja-JP')}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Featured Links Section */}
        <section className="relative">
          {/* CTA Section for Verification Data */}
          <div className="bg-rose-900 rounded-md p-8 mb-16 text-white shadow-2xl border border-rose-800">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4 text-rose-100">斎藤元彦 検証データ</h2>
              <p className="text-xl mb-6 text-rose-200">
                県政運営の問題点を事実に基づいて整理・追跡しています
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Link to="/verification?category=unanswered" className="block">
                  <div className="bg-red-400 rounded-md p-4 hover:bg-red-300 transition-all duration-300 shadow-lg cursor-pointer">
                    <div className="text-5xl font-bold text-white">
                      {verificationCounts.unanswered}
                      <span className="text-lg">件</span>
                    </div>
                    <div className="text-white text-base font-semibold">答えていないこと</div>
                  </div>
                </Link>
                <Link to="/verification?category=lies" className="block">
                  <div className="bg-orange-500 rounded-md p-4 hover:bg-orange-400 transition-all duration-300 shadow-lg cursor-pointer">
                    <div className="text-5xl font-bold text-white">
                      {verificationCounts.lies}
                      <span className="text-lg">件</span>
                    </div>
                    <div className="text-white text-base font-semibold">虚偽・誇張</div>
                  </div>
                </Link>
                <Link to="/verification?category=movements" className="block">
                  <div className="bg-blue-500 rounded-md p-4 hover:bg-blue-400 transition-all duration-300 shadow-lg cursor-pointer">
                    <div className="text-5xl font-bold text-white">
                      {verificationCounts.movements}
                      <span className="text-lg">件</span>
                    </div>
                    <div className="text-white text-base font-semibold">関連動向</div>
                  </div>
                </Link>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-rose-100 text-rose-900 hover:bg-white hover:text-rose-950 px-8 font-semibold shadow-2xl hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  <Link to="/verification">全データを見る</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Verification Data List Table */}
          <div className="bg-white rounded-lg p-6 mb-16 shadow-sm border">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">最新の検証データ一覧（クリックで詳細へ）</h3>
              <Link to="/verification" className="text-blue-600 hover:text-blue-700 font-medium">
                すべて見る →
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">カテゴリ</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">タイトル</th>
                  </tr>
                </thead>
                <tbody>
                  {verificationData.slice(0, 50).map((item, index) => {
                    // データタイプの表示名を取得
                    const getDataTypeName = (item) => {
                      if (item.data_type === 'unanswered' || item.category === '未回答事項') {
                        return '未回答事項';
                      } else if (item.data_type === 'lies' || item.category === '虚偽・誇張') {
                        return '虚偽・誇張';
                      } else if (item.data_type === 'movements' || item.category === '関連動向') {
                        return '関連動向';
                      }
                      return item.category || '未分類';
                    };

                    // カテゴリの色を取得
                    const getCategoryColor = (item) => {
                      if (item.data_type === 'unanswered' || item.category === '未回答事項') {
                        return 'bg-red-100 text-red-800 border-red-200';
                      } else if (item.data_type === 'lies' || item.category === '虚偽・誇張') {
                        return 'bg-orange-100 text-orange-800 border-orange-200';
                      } else if (item.data_type === 'movements' || item.category === '関連動向') {
                        return 'bg-blue-100 text-blue-800 border-blue-200';
                      }
                      return 'bg-gray-100 text-gray-800 border-gray-200';
                    };

                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer ${
                          index % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'
                        }`}
                      >
                        <td className="py-2 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(item)}`}>
                            {getDataTypeName(item)}
                          </span>
                        </td>
                        <td className="py-2 px-4">
                          <Link
                            to={`/verification?highlight=${item.id}#item-${item.id}`}
                            className="text-gray-900 hover:text-blue-600 font-medium text-base cursor-pointer transition-colors leading-tight"
                          >
                            {item.title}
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {verificationData.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">検証データがありません</p>
              </div>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/verification"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-7

              0 transition-colors font-medium"
              >
                全ての検証データを見る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>

          {/* Section Header with Decorative Line */}
          <div className="flex items-center mb-12">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-gray-300 dark:via-gray-600 dark:to-gray-600"></div>
            <div className="px-6">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white bg-white dark:bg-gray-900 px-4">
                注目のリンク
              </h2>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-transparent dark:from-gray-600 dark:to-transparent"></div>
            <Link to="/links?tab=便利リンク" className="ml-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
              すべて見る →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredLinks.map((link) => (
              <Card key={link.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <Tag variant="category">{link.category}</Tag>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2">
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 dark:hover:text-blue-400">
                      {link.title}
                    </a>
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-base mb-4 line-clamp-3">{link.summary}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{link.source_name}</span>
                    <div className="flex flex-wrap gap-1">
                      {(link.tags || []).slice(0, 2).map((tag) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* 掲示板メニューセクション */}
        <section className="relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full mb-4">
              <i className="ri-chat-3-line text-2xl text-slate-600 dark:text-slate-400"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">掲示板</h2>
            <div className="w-20 h-1 bg-slate-500 mx-auto rounded-full mb-4"></div>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              自由に議論・情報交換ができる掲示板です
            </p>
          </div>

          <div className="bg-slate-800 rounded-md p-8 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-700/20 via-transparent to-slate-900/20"></div>
            <div className="relative max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                <div className="bg-slate-700 rounded-md p-4 hover:bg-slate-600 transition-all duration-300 cursor-pointer border border-slate-600 hover:scale-105 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  <Link to="/forum?category=情報提供" className="block">
                    <div className="text-2xl mb-2 text-slate-200">
                      <i className="ri-information-line"></i>
                    </div>
                    <div className="text-slate-100 font-semibold">情報提供</div>
                    <div className="text-slate-400 text-sm">有益な情報をシェア</div>
                  </Link>
                </div>
                <div className="bg-slate-700 rounded-md p-4 hover:bg-slate-600 transition-all duration-300 cursor-pointer border border-slate-600 hover:scale-105 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  <Link to="/forum?category=リンク記載希望" className="block">
                    <div className="text-2xl mb-2 text-slate-200">
                      <i className="ri-link"></i>
                    </div>
                    <div className="text-slate-100 font-semibold">リンク記載希望</div>
                    <div className="text-slate-400 text-sm">追加希望の情報</div>
                  </Link>
                </div>
                <div className="bg-slate-700 rounded-md p-4 hover:bg-slate-600 transition-all duration-300 cursor-pointer border border-slate-600 hover:scale-105 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  <Link to="/forum?category=ご意見" className="block">
                    <div className="text-2xl mb-2 text-slate-200">
                      <i className="ri-chat-3-line"></i>
                    </div>
                    <div className="text-slate-100 font-semibold">ご意見</div>
                    <div className="text-slate-400 text-sm">建設的な意見交換</div>
                  </Link>
                </div>
                <div className="bg-slate-700 rounded-md p-4 hover:bg-slate-600 transition-all duration-300 cursor-pointer border border-slate-600 hover:scale-105 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  <Link to="/forum?category=雑談" className="block">
                    <div className="text-2xl mb-2 text-slate-200">
                      <i className="ri-chat-smile-2-line"></i>
                    </div>
                    <div className="text-slate-100 font-semibold">雑談</div>
                    <div className="text-slate-400 text-sm">気軽な話題</div>
                  </Link>
                </div>
                <div className="bg-slate-700 rounded-md p-4 hover:bg-slate-600 transition-all duration-300 cursor-pointer border border-slate-600 hover:scale-105 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                  <Link to="/forum?category=その他" className="block">
                    <div className="text-2xl mb-2 text-slate-200">
                      <i className="ri-more-line"></i>
                    </div>
                    <div className="text-slate-100 font-semibold">その他</div>
                    <div className="text-slate-400 text-sm">その他の話題</div>
                  </Link>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-slate-3
                  text-slate-200 hover:bg-slate-100 hover:text-slate-800 px-8 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  <Link to="/forum">新規投稿する</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
